
import React, { useMemo } from 'react';
import { TrendingUp, Users, Building } from 'lucide-react';

interface ComparisonIndicatorProps {
  score: number;
  businessType: string;
  employeeCount: string;
}

export const ComparisonIndicator: React.FC<ComparisonIndicatorProps> = ({ 
  score, 
  businessType, 
  employeeCount 
}) => {
  // Use useMemo to calculate benchmark data once and prevent re-renders
  const benchmark = useMemo(() => {
    // Create deterministic "random" values based on business type and employee count
    const seedValue = (businessType + employeeCount).split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    const normalizedSeed = (seedValue % 100) / 100; // Convert to 0-1 range
    
    // Calculate percentile based on score with some deterministic variation
    const basePercentile = Math.min(85, Math.max(15, score + (normalizedSeed - 0.5) * 20));
    
    // Calculate industry average
    const industryAverage = Math.max(30, Math.min(70, score - 10 + (normalizedSeed - 0.5) * 20));
    
    // Calculate total companies based on business type
    const companyCountSeed = businessType.length * employeeCount.length;
    const totalCompanies = 200 + (companyCountSeed % 500);
    
    return {
      percentile: Math.round(basePercentile),
      industryAverage: Math.round(industryAverage),
      totalCompanies
    };
  }, [score, businessType, employeeCount]);

  const isAboveAverage = score > benchmark.industryAverage;

  return (
    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 bg-energy-blue/10 rounded-lg flex items-center justify-center">
          <TrendingUp className="w-5 h-5 text-energy-blue" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900">Benchmark Vergelijking</h3>
      </div>

      <div className="space-y-4">
        {/* Percentile comparison */}
        <div className="bg-gradient-to-r from-energy-green/10 to-energy-blue/10 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">Je prestatie</span>
            <span className="text-lg font-bold text-energy-green">
              Beter dan {benchmark.percentile}%
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-gradient-to-r from-energy-green to-energy-blue h-2 rounded-full transition-all duration-1000"
              style={{ width: `${benchmark.percentile}%` }}
            ></div>
          </div>
        </div>

        {/* Industry average */}
        <div className="flex items-center justify-between py-3 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <Building className="w-4 h-4 text-gray-500" />
            <span className="text-sm text-gray-600">Industrie gemiddelde</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-lg font-semibold">{benchmark.industryAverage}%</span>
            <div className={`px-2 py-1 rounded-full text-xs font-medium ${
              isAboveAverage 
                ? 'bg-green-100 text-green-800' 
                : 'bg-orange-100 text-orange-800'
            }`}>
              {isAboveAverage ? '+' : '-'}{Math.abs(score - benchmark.industryAverage)}%
            </div>
          </div>
        </div>

        {/* Company size comparison */}
        <div className="flex items-center justify-between py-3">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-gray-500" />
            <span className="text-sm text-gray-600">Vergelijkbare bedrijven</span>
          </div>
          <span className="text-sm font-medium text-gray-700">
            {benchmark.totalCompanies} bedrijven
          </span>
        </div>

        {/* Insight message */}
        <div className="bg-energy-blue/5 border border-energy-blue/20 rounded-lg p-3 mt-4">
          <p className="text-sm text-energy-blue">
            💡 <strong>Insight:</strong> Bedrijven in de {businessType.toLowerCase()} met {employeeCount} werknemers 
            scoren gemiddeld {benchmark.industryAverage}%. Je {isAboveAverage ? 'overtreft' : 'blijft onder'} dit gemiddelde!
          </p>
        </div>
      </div>
    </div>
  );
};
